/**
 * useOrders Hook
 *
 * Thin wrapper around useResourceList for orders.
 * Provides type safety and order-specific defaults.
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { getOrders, ordersQueryKeys } from '@api/orders';
import type { PaginatedResponse, ResourceParams } from '../useResourceList';
import type { Order } from '@api/orders';

export type OrdersResponse = PaginatedResponse<Order>;

export interface UseOrdersParams extends ResourceParams {
  /** Filter by one or more order statuses */
  order_statuses?: string[];
  /** Filter by salesperson IDs */
  salesperson_ids?: string[];
  /** Search query */
  q?: string;
}

const DEFAULT_STALE_TIME = 30_000;

/**
 * Fetch orders with optional filtering, sorting, and pagination.
 *
 * @example
 * const { data, isLoading } = useOrders({ order_statuses: ['confirmed'], page: 1 });
 */
export function useOrders(params: UseOrdersParams = {}, initialData?: OrdersResponse) {
  const { salesperson_ids, order_statuses, ...restParams } = params;
  const apiParams: ResourceParams = {
    ...restParams,
    salesperson_id: salesperson_ids?.length ? salesperson_ids : undefined,
    order_status: order_statuses?.length ? order_statuses : undefined,
  };

  return useQuery({
    queryKey: ordersQueryKeys.list(apiParams),
    queryFn: () => getOrders(apiParams),
    initialData,
    placeholderData: keepPreviousData,
    staleTime: DEFAULT_STALE_TIME,
  });
}
